import React, { useState } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";
import {
  faEnvelope,
  faUser,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-regular-svg-icons";
import "./SignUp.css";
import "../../forms/form-field.css";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSign } from "../../hooks/useSign";
import { InputGroup } from "react-bootstrap";
import { useLogin } from "../../hooks/useLogin";
import FormField from "../../forms/FormField";
import { signUpSchema } from "../../forms/schemas";

function SignUp(props) {
  const [showPassword, setShowPassword] = useState(false);
  const { signUp } = useSign();
  const { logIn } = useLogin();

  // KHAN-146: igual que en LogIn, react-hook-form + Zod reemplazan el useState
  // por campo, el useEffect sin dependencias que reescribia clases del DOM en
  // cada render, y la validacion que se limitaba al `required` de HTML. Ahora
  // el largo minimo de contrasena se valida antes de llegar al servidor.
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    resolver: zodResolver(signUpSchema),
    mode: "onTouched",
    defaultValues: { name: "", lastname: "", email: "", password: "" },
  });

  const togglePassword = () => setShowPassword((visible) => !visible);

  const onSubmit = async ({ name, lastname, email, password }) => {
    const res = await signUp(name, lastname, email, password);

    if (res === "success") {
      const loginRes = await logIn(email, password);
      window.location.href = loginRes === "noRoles" ? "/register" : "/dashboard";
      return;
    }

    // El codigo anterior mostraba esto con un alert() y un comentario pidiendo
    // justamente cambiarlo ("cambiar esta alerta a futuro para mostrar los
    // errores de mejor manera"). El error del backend suele ser sobre el email
    // (tipicamente que ya esta registrado), asi que se muestra en ese campo.
    setError("email", {
      type: "server",
      message:
        typeof res === "string" && res.length
          ? res
          : "No se pudo completar el registro. Intenta de nuevo.",
    });
  };

  return (
    <div className="signup-form">
      <div className="signup-form-body">
        <Form
          onSubmit={handleSubmit(onSubmit)}
          className="signup-form-content"
          noValidate
        >
          <h1 className="signup-form-title">Are you new?</h1>
          <h5 className="signup-form-subtitle">
            Add your information to register.
          </h5>
          <div className="signup-form-inputs">
            <FormField
              id="singup-nameInput"
              label="Your name"
              icon={<FontAwesomeIcon className="signup-icons" icon={faUser} />}
              type="text"
              placeholder="Your name here"
              className="signup-form-control-lg"
              error={errors.name}
              registration={register("name")}
            />
            <FormField
              id="singup-lastnameInput"
              label="Your last name"
              icon={<FontAwesomeIcon className="signup-icons" icon={faUser} />}
              type="text"
              placeholder="Your last name here"
              className="signup-form-control-lg"
              error={errors.lastname}
              registration={register("lastname")}
            />
            <FormField
              id="singup-emailInput"
              label="Email"
              icon={<FontAwesomeIcon className="signup-icons" icon={faEnvelope} />}
              type="email"
              placeholder="example@mep.co.cr"
              className="signup-form-control-lg"
              error={errors.email}
              registration={register("email")}
            />
            <FormField
              id="singup-passwordInput"
              label="Password"
              icon={<FontAwesomeIcon className="signup-icons" icon={faLock} />}
              type={showPassword ? "text" : "password"}
              placeholder="Your password"
              className="signup-form-control-lg"
              style={{ borderRightWidth: 0 }}
              error={errors.password}
              registration={register("password")}
            >
              {(control) => (
                <InputGroup className="form-input-group">
                  {control}
                  <InputGroup.Text
                    id="singup-inputGroupText"
                    className="form-input-group-text-password"
                  >
                    <Button
                      id="singup-passwordButton"
                      className="signup-form-password-button"
                      onClick={togglePassword}
                      type="button"
                      aria-label={
                        showPassword ? "Ocultar contrasena" : "Mostrar contrasena"
                      }
                    >
                      {showPassword ? (
                        <FontAwesomeIcon className="fa-xl float end" icon={faEye} />
                      ) : (
                        <FontAwesomeIcon
                          className="fa-xl float end"
                          icon={faEyeSlash}
                        />
                      )}
                    </Button>
                  </InputGroup.Text>
                </InputGroup>
              )}
            </FormField>
            <Button
              id="singup-submit-button"
              className={
                isValid ? "signup-form-button-filled" : "signup-form-button-empty"
              }
              type="submit"
              disabled={isSubmitting}
            >
              Sign Up
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}

export default SignUp;
